import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { Icon, Text, Button, Tooltip } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import
{
  DatabasePreviewPannelProps,
  TableRecordsResponse,
  QueryResponse,
  DatabasePreviewPannelMode
} from './DatabasePreviewPannel.type';
import { DataTable } from './DataTable';
import api from '../../utils/axios';
import { Permissions } from '../../constants';

const DEFAULT_COL_WIDTH = 160;

export const DatabasePreviewPannel: React.FC<DatabasePreviewPannelProps> = ({ projectId, permissions, onClose }) => {
  const [mode, setMode] = useState<DatabasePreviewPannelMode>(DatabasePreviewPannelMode.BROWSE);

  // ── Browse state ──
  const [tables, setTables] = useState<{[schema: string]: string[];} | undefined>(undefined);
  const [selectedTable, setSelectedTable] = useState<{schema: string; table: string} | null>(null);
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableRecordsResponse | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [searchQuery, setSearchQuery] = useState('');

  // ── Query state ──
  const [sql, setSql] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState<QueryResponse | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);

  // ── Column resizing ──
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);

  const hasUpdatePermission = useMemo(() => {
    return permissions.includes(Permissions.UpdateProjectPermission);
  }, [permissions]);

  const getColWidth = (colKey: string) => columnWidths[colKey] ?? DEFAULT_COL_WIDTH;

  const handleResizeStart = (e: React.MouseEvent, colKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = getColWidth(colKey);
    resizingRef.current = { col: colKey, startX: e.clientX, startWidth };

    const onMouseMove = (ev: MouseEvent) => {
      const current = resizingRef.current;
      if (!current) return;
      const delta = ev.clientX - current.startX;
      const newWidth = Math.max(60, current.startWidth + delta);
      const colKey = current.col;
      setColumnWidths((prev) => ({ ...prev, [colKey]: newWidth }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Reset column widths when table changes
  useEffect(() => {
    setColumnWidths({});
  }, [selectedTable]);

  const copyToClipboard = useCallback(async (value: any) => {
    const text = value === null || value === undefined ? 'NULL' : String(value);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // silently fail
    }
  }, []);

  const renderCellTooltipContent = useCallback((value: any, onClose: () => void) => (
    <button
      type='button'
      onClick={() => {
        copyToClipboard(value);
        onClose();
      }}
      className='flex items-center gap-bg-v2-xs text-left text-fg-primary hover:bg-bg-secondary rounded px-1 py-0.5 -mx-1 transition-colors'
    >
      <Icon name={IconName.Copy} size='xs' variant='secondary' />
      <span className='text-xs'>Copy value</span>
    </button>
  ), [copyToClipboard]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/projects/${projectId}/tables`);
      setTables(response.data || {});
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 503) {
        setError('Not able to connect to Database.');
      } else {
        setError(err?.response?.data?.detail || 'Failed to load tables');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async () => {
    if (!selectedTable) return;
    try {
      setDataLoading(true);
      const page = pagination.pageIndex + 1;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pagination.pageSize.toString(),
      });
      const response = await api.get(
        `/projects/${projectId}/tables/${selectedTable.schema}/${selectedTable.table}/records?${params.toString()}`
      );
      setTableData(response.data);
    } catch (err: any) {
      setTableData(null);
      const status = err?.response?.status;
      if (status === 503) {
        setError('Not able to connect to Database.');
      }
    } finally {
      setDataLoading(false);
    }
  };

  const runQuery = useCallback(async () => {
    const trimmed = sql.trim();
    if (!trimmed) return;
    try {
      setQueryLoading(true);
      setQueryError(null);
      setQueryResult(null);
      const response = await api.post(`/projects/${projectId}/query`, { sql: trimmed });
      setQueryResult(response.data);
    } catch (err: any) {
      setQueryError(err?.response?.data?.detail || 'Query failed');
    } finally {
      setQueryLoading(false);
    }
  }, [sql, projectId]);

  useEffect(() => {
    fetchTables();
  }, [projectId]);

  useEffect(() => {
    if (tables && Object.keys(tables).length > 0 && expandedSchemas.size === 0) {
      const firstSchema = Object.keys(tables)[0];
      setExpandedSchemas(new Set([firstSchema]));
    }
  }, [tables]);

  useEffect(() => {
    if (!selectedTable || mode !== 'browse') return;
    fetchTableData();
  }, [selectedTable, pagination, projectId, mode]);

  const filteredTables = useMemo(() => {
    if (!searchQuery.trim()) return tables;
    const filtered: {[schema: string]: string[]} = {};
    Object.entries(tables ?? {}).forEach(([schema, tableList]) => {
      const matchingTables = tableList.filter((t) =>
        t.toLowerCase().includes(searchQuery.toLowerCase()) ||
        schema.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (matchingTables.length > 0) {
        filtered[schema] = matchingTables;
      }
    });
    return filtered;
  }, [tables, searchQuery]);

  const handleTableClick = (schema: string, tableName: string) => {
    setSelectedTable({schema, table: tableName});
    setPagination({ pageIndex: 0, pageSize: 50 });
  };

  const toggleSchema = (schema: string) => {
    setExpandedSchemas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(schema)) {
        newSet.delete(schema);
      } else {
        newSet.add(schema);
      }
      return newSet;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      runQuery();
    }
  };

  const totalPages = tableData ? Math.ceil(tableData.total / pagination.pageSize) : 0;
  const startRow = pagination.pageIndex * pagination.pageSize + 1;
  const endRow = Math.min((pagination.pageIndex + 1) * pagination.pageSize, tableData?.total ?? 0);

  return (
    <div className='flex flex-col h-full bg-bg-primary'>

      {/* ── Top Header ── */}
      <div className='flex items-center justify-between w-full px-bg-v2-xl py-bg-v2-xl border-b border-border-primary shrink-0'>
        <div className='flex items-center gap-bg-v2-xl'>
          <Icon name={IconName.Layout} size='sm' variant='secondary' />
          <Text variant='text-sm' weight='semibold' color='primary'>Database Viewer</Text>
        </div>
        <div className='flex items-center gap-bg-v2-md'>
          {/* Mode tabs */}
          <div className='flex items-center bg-bg-secondary rounded-bg-v2-md p-0.5'>
            <button
              onClick={() => setMode(DatabasePreviewPannelMode.BROWSE)}
              className={`flex items-center gap-bg-v2-xs px-bg-v2-md py-bg-v2-xs rounded-bg-v2-sm text-xs font-medium transition-colors ${
                mode === 'browse'
                  ? 'bg-bg-primary text-fg-primary shadow-sm'
                  : 'text-fg-tertiary hover:text-fg-secondary'
              }`}
            >
              <Icon name={IconName.Table} size='xs' variant={mode === 'browse' ? 'secondary' : 'secondary'} />
              Browse
            </button>
            {hasUpdatePermission && <button
              onClick={() => setMode(DatabasePreviewPannelMode.QUERY)}
              className={`flex items-center gap-bg-v2-xs px-bg-v2-md py-bg-v2-xs rounded-bg-v2-sm text-xs font-medium transition-colors ${
                mode === 'query'
                  ? 'bg-bg-primary text-fg-primary shadow-sm'
                  : 'text-fg-tertiary hover:text-fg-secondary'
              }`}
            >
              <Icon name={IconName.Code} size='xs' variant='secondary' />
              Query
            </button>}
          </div>
          <Button
            label={<Icon name={IconName.Close} size='sm' variant='secondary' />}
            variant='ghost'
            size='small'
            onClick={onClose}
            className='!rounded-full !p-bg-v2-sm'
          />
        </div>
      </div>

      {/* ── Body ── */}
      <div className='flex-1 flex min-h-0'>

        {error ? (
          <div className='flex-1 flex flex-col items-center justify-center gap-bg-v2-md'>
            <Icon name={IconName.Alert} size='xl' variant='error' />
            <Text variant='text-sm' color='tertiary'>{error}</Text>
            <Button label='Retry' variant='outline' size='small' onClick={fetchTables} />
          </div>
        ) : (
          <>
            {/* ── Left: Table List (always visible) ── */}
            <div className='w-56 shrink-0 flex flex-col border-r border-border-primary'>
              <div className='px-bg-v2-xl h-10 flex items-center justify-between border-b border-border-primary shrink-0'>
                <Text variant='text-xs' weight='semibold' color='secondary'>TABLES</Text>
                {!loading && (
                  <span className='text-[11px] leading-none text-fg-tertiary bg-bg-secondary rounded-full px-1.5 py-0.5 font-medium'>
                    {Object.values(tables ?? {}).flat().length}
                  </span>
                )}
              </div>
              <div className='px-bg-v2-md py-bg-v2-sm shrink-0 border-b border-border-primary'>
                <div className='flex items-center gap-bg-v2-sm bg-bg-secondary rounded-bg-v2-md px-bg-v2-md py-bg-v2-xs'>
                  <Icon name={IconName.Search} size='xs' variant='secondary' />
                  <input
                    type='text'
                    placeholder='Filter tables...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className='flex-1 bg-transparent text-xs text-fg-primary placeholder:text-fg-tertiary outline-none border-none min-w-0'
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className='text-fg-tertiary hover:text-fg-primary transition-colors'>
                      <Icon name={IconName.Close} size='xs' variant='secondary' />
                    </button>
                  )}
                </div>
              </div>
              <div className='flex-1 overflow-y-auto modern-scrollbar-spaced py-bg-v2-sm'>
                {loading ? (
                  <div className='flex flex-col gap-bg-v2-xs px-bg-v2-md pt-bg-v2-sm'>
                    {[...Array(7)].map((_, i) => (
                      <div key={i} className='h-8 rounded-bg-v2-md bg-bg-secondary skeleton-shimmer' />
                    ))}
                  </div>
                ) : Object.keys(filteredTables ?? {}).length === 0 ? (
                  <div className='flex flex-col items-center justify-center py-bg-v2-xl gap-bg-v2-sm px-bg-v2-md text-center'>
                    <Icon name={IconName.Table} size='lg' variant='secondary' />
                    <Text variant='text-xs' color='tertiary'>
                      {searchQuery ? 'No tables match your filter' : 'No tables found'}
                    </Text>
                  </div>
                ) : (
                  <div className='px-bg-v2-sm flex flex-col gap-bg-v2-xs'>
                    {Object.entries(filteredTables ?? {}).map(([schema, tableList]) => {
                      const isExpanded = expandedSchemas.has(schema);
                      const isActive = selectedTable?.schema === schema;
                      return (
                        <div key={schema} className='flex flex-col'>
                          {/* Schema Header */}
                          <div
                            onClick={() => toggleSchema(schema)}
                            className={`flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm rounded-bg-v2-md cursor-pointer transition-colors hover:bg-bg-secondary ${isExpanded || isActive ? 'bg-bg-secondary' : ''}`}
                          >
                            <Icon
                              name={IconName.ChevronRight}
                              size='sm'
                              inheritColor
                              className={` ${isExpanded ? 'rotate-90' : ''}`}
                              variant='secondary'
                            />
                            <Icon name={IconName.Schema} size='sm'  variant='secondary' />
                            <Text variant='text-sm' weight='semibold' color='secondary' className='truncate'>
                              {schema}
                            </Text>
                            <span className='text-[11px] leading-none text-fg-tertiary bg-bg-secondary rounded-full px-1.5 py-0.5 font-medium ml-auto'>
                              {tableList.length}
                            </span>
                          </div>

                          {/* Tables List */}
                          {isExpanded && (
                            <div className='flex flex-col pl-bg-v2-xl'>
                              {tableList.map((tableName) => {
                                const isActive = selectedTable?.schema === schema && selectedTable?.table === tableName;
                                return (
                                  <div
                                    key={`${schema}.${tableName}`}
                                    onClick={() => {
                                      if (mode === 'browse') {
                                        handleTableClick(schema, tableName);
                                      } else {
                                        setSelectedTable({schema, table: tableName});
                                        setSql(`SELECT * FROM "${schema}"."${tableName}" LIMIT 100;`);
                                        setQueryResult(null);
                                        setQueryError(null);
                                      }
                                    }}
                                    className={`flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm rounded-bg-v2-md cursor-pointer transition-colors ${
                                      isActive ? 'bg-bg-brand-primary-alt' : 'hover:bg-bg-secondary'
                                    }`}
                                  >
                                    <Icon name={IconName.Table} size='sm' className='shrink-0' variant={isActive ? 'brand' : 'secondary'} />
                                    <Tooltip content={tableName} position='right'>
                                      <Text
                                        variant='text-sm'
                                        weight={isActive ? 'semibold' : 'regular'}
                                        color={isActive ? 'brand-primary' : 'primary'}
                                        className='truncate'
                                      >
                                        {tableName}
                                      </Text>
                                    </Tooltip>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: Browse or Query panel ── */}
            <div className='flex-1 flex flex-col min-w-0 min-h-0'>

              {/* ══ BROWSE MODE ══ */}
              {mode === 'browse' && (
                !selectedTable ? (
                  <div className='flex-1 flex flex-col items-center justify-center gap-bg-v2-md text-center px-bg-v2-xl'>
                    <Icon name={IconName.DataModelPreview} size='xxl' variant='secondary' />
                    <Text variant='text-sm' color='tertiary'>Select a table from the left to explore its data</Text>
                  </div>
                ) : (
                  <>
                    <div className='flex items-center justify-between px-bg-v2-xl h-10 border-b border-border-primary shrink-0'>
                      <div className='flex items-center gap-bg-v2-md min-w-0'>
                        <div className='flex items-center gap-bg-v2-sm min-w-0'>
                          <Icon name={IconName.Table} size='sm' variant='secondary' />
                          <Text variant='text-sm' weight='semibold' color='primary' className='truncate'>
                            {selectedTable.table}
                          </Text>
                        </div>
                        {tableData && !dataLoading && (
                          <div className='flex items-center gap-bg-v2-sm shrink-0'>
                            <span className='h-1 w-1 rounded-full bg-fg-tertiary' />
                            <Text variant='text-xs' color='tertiary'>{tableData.total.toLocaleString()} rows</Text>
                            <span className='h-1 w-1 rounded-full bg-fg-tertiary' />
                            <Text variant='text-xs' color='tertiary'>{tableData.columns.length} columns</Text>
                          </div>
                        )}
                      </div>
                      <Button
                        label={<Icon name={IconName.Reload} size='sm' variant='secondary' spin={dataLoading} />}
                        variant='ghost'
                        size='very-small'
                        onClick={fetchTableData}
                        disabled={dataLoading}
                        className='px-bg-v2-xs h-7 min-h-0 w-7 shrink-0'
                        title='Refresh'
                      />
                    </div>
                    {dataLoading ? (
                      <DataTable
                        columns={tableData?.columns ?? []}
                        rows={[]}
                        columnWidths={columnWidths}
                        onResizeStart={handleResizeStart}
                        renderCellTooltipContent={renderCellTooltipContent}
                        tooltipTrigger='contextmenu'
                        isLoading
                      />
                    ) : tableData ? (
                      <div key={`${selectedTable}-${pagination.pageIndex}`} className='flex-1 flex flex-col min-h-0 animate-fade-in'>
                        <DataTable
                          columns={tableData.columns}
                          rows={tableData.rows}
                          columnWidths={columnWidths}
                          onResizeStart={handleResizeStart}
                          startRowIndex={startRow}
                          renderCellTooltipContent={renderCellTooltipContent}
                          tooltipTrigger='contextmenu'
                        />
                        {totalPages > 1 && (
                          <div className='flex items-center justify-between px-bg-v2-xl py-bg-v2-md border-t border-border-primary shrink-0 bg-bg-secondary'>
                            <Text variant='text-xs' color='tertiary'>
                              Showing {startRow.toLocaleString()}–{endRow.toLocaleString()} of {tableData.total.toLocaleString()} rows
                            </Text>
                            <div className='flex items-center gap-bg-v2-sm'>
                              <Button
                                label={<Icon name={IconName.Backward} size='sm' variant='secondary' />}
                                variant='ghost'
                                size='very-small'
                                disabled={pagination.pageIndex === 0}
                                onClick={() => setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))}
                                className='px-bg-v2-xs h-7 min-h-0 w-7'
                              />
                              <Text variant='text-xs' color='secondary'>{pagination.pageIndex + 1} / {totalPages}</Text>
                              <Button
                                label={<Icon name={IconName.Forward} size='sm' variant='secondary' />}
                                variant='ghost'
                                size='very-small'
                                disabled={pagination.pageIndex >= totalPages - 1}
                                onClick={() => setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))}
                                className='px-bg-v2-xs h-7 min-h-0 w-7'
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </>
                )
              )}

              {/* ══ QUERY MODE ══ */}
              {mode === 'query' && (
                <>
                  {/* Editor area */}
                  <div className='shrink-0 flex flex-col border-b border-border-primary' style={{ minHeight: '140px', maxHeight: '240px' }}>
                    <div className='flex items-center justify-between px-bg-v2-xl h-10 border-b border-border-primary shrink-0'>
                      <div className='flex items-center gap-bg-v2-sm'>
                        <Icon name={IconName.Code} size='sm' variant='secondary' />
                        <Text variant='text-xs' weight='semibold' color='secondary'>SQL EDITOR</Text>
                      </div>
                      <div className='flex items-center gap-bg-v2-sm'>
                        <Text variant='text-xs' color='tertiary' className='select-none'>⌘ + Enter to run</Text>
                        <Button
                          label={
                            queryLoading
                              ? <div className='flex items-center gap-bg-v2-xs'>
                                  <Icon name={IconName.Loading} size='xs' variant='secondary' spin />
                                  <span className='text-xs'>Running…</span>
                                </div>
                              : <div className='flex items-center gap-bg-v2-xs'>
                                  <Icon name={IconName.Run} size='xs' inheritColor />
                                  <span className='text-xs'>Run</span>
                                </div>
                          }
                          variant='primary'
                          size='small'
                          onClick={runQuery}
                          disabled={queryLoading || !sql.trim() || !hasUpdatePermission}
                        />
                      </div>
                    </div>
                    <div className='flex-1 relative overflow-hidden'>
                      <textarea
                        value={sql}
                        onChange={(e) => setSql(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={!hasUpdatePermission}
                        placeholder={'-- Write your SQL query here\nSELECT * FROM your_table LIMIT 100;'}
                        spellCheck={false}
                        className='absolute inset-0 w-full h-full resize-none bg-bg-primary text-xs text-fg-primary font-mono placeholder:text-fg-tertiary outline-none border-none p-bg-v2-md leading-relaxed'
                      />
                    </div>
                  </div>

                  {/* Results area */}
                  <div className='flex-1 flex flex-col min-h-0'>
                    {!queryResult && !queryError && !queryLoading && (
                      <div className='flex-1 flex flex-col items-center justify-center gap-bg-v2-sm text-center px-bg-v2-xl'>
                        <Icon name={IconName.DataModelPreview} size='xxl' variant='secondary' />
                        <Text variant='text-sm' color='tertiary'>Run a query to see results here</Text>
                      </div>
                    )}
                    {queryLoading && (
                      <DataTable
                        columns={queryResult?.columns?.map((c) => ({ name: c })) ?? []}
                        rows={[]}
                        columnWidths={columnWidths}
                        getColumnKey={(col) => `q_${col.name}`}
                        isLoading
                      />
                    )}
                    {queryError && !queryLoading && (
                      <div className='flex-1 flex flex-col p-bg-v2-xl gap-bg-v2-md'>
                        <div className='flex items-center gap-bg-v2-sm'>
                          <Icon name={IconName.Alert} size='sm' variant='error' />
                          <Text variant='text-xs' weight='semibold' color='error'>Query Error</Text>
                        </div>
                        <div className='bg-bg-secondary rounded-bg-v2-md p-bg-v2-md border border-border-primary'>
                          <Text variant='text-xs' color='tertiary' className='font-mono whitespace-pre-wrap break-words'>{queryError}</Text>
                        </div>
                      </div>
                    )}
                    {queryResult && !queryLoading && (
                      <div key={queryResult.row_count + queryResult.columns.join()} className='flex-1 flex flex-col min-h-0 animate-fade-in'>
                        <div className='flex items-center justify-between px-bg-v2-xl h-10 border-b border-border-primary shrink-0 bg-bg-secondary'>
                          <div className='flex items-center gap-bg-v2-sm'>
                            <Icon name={IconName.CheckCircle} size='xs' variant='success' />
                            <Text variant='text-xs' color='secondary'>
                              {queryResult.affected_rows != null
                                ? `${queryResult.affected_rows} ${queryResult.affected_rows === 1 ? 'row' : 'rows'} affected`
                                : `${queryResult.row_count.toLocaleString()} ${queryResult.row_count === 1 ? 'row' : 'rows'} returned`}
                            </Text>
                          </div>
                          <Button
                            label={<Icon name={IconName.Close} size='xs' variant='secondary' />}
                            variant='ghost'
                            size='very-small'
                            onClick={() => { setQueryResult(null); setQueryError(null); }}
                            className='px-bg-v2-xs h-6 min-h-0 w-6'
                            title='Clear results'
                          />
                        </div>
                        {queryResult.affected_rows != null || queryResult.row_count === 0 ? (
                          <div className='flex-1 flex flex-col items-center justify-center gap-bg-v2-sm text-center px-bg-v2-xl'>
                            <Icon name={IconName.CheckCircle} size='xl' variant='success' />
                            {queryResult.affected_rows != null ? (
                              <>
                                <Text variant='text-sm' color='primary' weight='semibold'>Query executed successfully</Text>
                                <Text variant='text-xs' color='tertiary'>
                                  {queryResult.affected_rows} {queryResult.affected_rows === 1 ? 'row' : 'rows'} affected
                                </Text>
                              </>
                            ) : (
                              <Text variant='text-sm' color='tertiary'>Query returned no rows</Text>
                            )}
                          </div>
                        ) : (
                          <DataTable
                            columns={queryResult.columns.map((c) => ({ name: c }))}
                            rows={queryResult.rows}
                            columnWidths={columnWidths}
                            onResizeStart={handleResizeStart}
                            startRowIndex={1}
                            renderCellTooltipContent={renderCellTooltipContent}
                            tooltipTrigger='contextmenu'
                            getColumnKey={(col) => `q_${col.name}`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
